import React from 'react';

import Error from 'next/error';

import { withCtxMiddleware } from '@bonnet/next/ctx-middleware';
import { withCtxAbortOnStatusCode } from '@bonnet/next/ctx-middleware/with-ctx-abort-on-status-code';
import { withBonnetPage } from '@bonnet/next/page';
import { useRouterScroll } from '@bonnet/next/use-router-scroll';

import { pageNames } from '@atc/bonnet-paths';
import { useFeatures } from '@atc/features';

import usePauseAudioEye from '@/utilities/usePauseAudioEye';

import {
    ctxPresetFindCar,
    withCustomLeadRouting,
    withDataIslandDispatcher,
    withInterestRates,
    withPreloadHeader,
    withWalletConfigs,
} from '@/context-middleware';

import {
    addBuildAndPricePageData,
    dispatchBuildAndPricePageData,
} from '@/context-middleware/bap';
import {
    withRequestOptions,
} from '@/context-middleware/srp';

import * as bapDucks from '@/ducks/bap';
import {
    srpResultsDuck,
    srpReuseActiveStateDuck,
} from '@/ducks/srp';
import {
    vdpResultsDuck,
} from '@/ducks/vdp';

import {
    CanonicalUrlModule,
    ParsedQueryModule,
    PersonalizationEngineModule,
    SearchLinkModule,
} from '@/modules';

import {
    CommonHeadContainer,
} from '@/containers';

import BAPAnalytics from '@/containers/bap/BAPAnalytics';
import BapPageEventsContainer from '@/containers/bap/BapPageEventsContainer';
import BuildAndPricePageContainer from '@/containers/bap/BuildAndPricePageContainer';
import PersonalizationEngineContainer from '@/containers/PersonalizationEngineContainer';

const { BUILD_PRICE } = pageNames;

function BuildAndPricePage({
    statusCode,
    query,
    currentUrl,
}) {
    const {
        personalization_engine: [enablePersonalizationEngine],
    } = useFeatures([
        'personalization_engine',
    ]);

    usePauseAudioEye();

    useRouterScroll();

    if (statusCode) {
        return <Error statusCode={statusCode} />;
    }

    return (
        <>
            {enablePersonalizationEngine && <PersonalizationEngineContainer pageName="build-and-price" />}
            <CommonHeadContainer
                currentUrl={currentUrl}
                pageName={BUILD_PRICE}
                query={query}
            />
            <BapPageEventsContainer />
            <BuildAndPricePageContainer />
            <BAPAnalytics />
        </>
    );
}

BuildAndPricePage.getInitialProps = async (ctx) => {

    if (srpResultsDuck.selectors.hasActiveResults(ctx.store.getState())) {
        ctx.store.dispatch(srpReuseActiveStateDuck.creators.setTrue());
    }

    await withCtxMiddleware([
        ctxPresetFindCar({
            pageName: BUILD_PRICE,
            preFetchCtxMiddleware: [
                withRequestOptions(),
                ctx.withModuleCtxMiddleware({
                    batchDispatch: false,
                    fetchCompositeJSON: false,
                }),
            ],
            fetcher: addBuildAndPricePageData,
            dispatcher: dispatchBuildAndPricePageData,
        }),
        withCtxAbortOnStatusCode(),
        withPreloadHeader(),
        withInterestRates(),
        withWalletConfigs(),
        withCustomLeadRouting(),
        withDataIslandDispatcher(),
    ], ctx);

    // if the current ctx contains a status code then we need to abort the process and return it
    // to allow the page to render an error
    if (ctx.statusCode) {
        return {
            statusCode: ctx.statusCode,
        };
    }

    return {
        query: ctx.query,
        currentUrl: ctx.data.currentUrl,
    };
};

const { BonnetPageComponent } = withBonnetPage(BuildAndPricePage, {
    reducers: [
        ...Object.values(bapDucks),
        srpResultsDuck,
        srpReuseActiveStateDuck,
        vdpResultsDuck,
    ],
    modules: [
        SearchLinkModule,
        CanonicalUrlModule,
        ParsedQueryModule,
        PersonalizationEngineModule,
    ],
});

export default BonnetPageComponent;
